import IconArrow, { IconArrowLeft } from "../icons/IconArrow";
import Flex from "../Flex/Flex";
import Select from "../forms/Select/Select";
import Button from "../forms/Button/Button";
import InputNumber from "../forms/InputNumber/InputNumber";
import styles from "./Pagination.module.css";
import { clamp } from "@/utils/dateUtils";

interface PaginationProps {
  total: number; // total number of results, not # of pages
  pageSizeOptions?: number[]; // number of results per page
  page: number;
  onChangePage: (page: number) => void;
  allowEmptyNext?: boolean; // still show the 'next' arrow even if there's not enough results for the next page. 
  // this allows you to interface with backend pagination. default falsy/null

  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  showPageSizeOptions?: boolean; // default true
  showPageSelector?: boolean; // default true

  sticky?: boolean; // should the pagination show as a sticky banner at the bottom of its container, default falsy
}

const Pagination = (props: PaginationProps) => {
  const startIndex = props?.total === 0 ? 0 : (props.page - 1) * props.pageSize + 1;
  const endIndex = Math.min(startIndex + props.pageSize - 1, props.total);
  const showPageSizeOptions = props.showPageSizeOptions === undefined ? true : props.showPageSizeOptions; // default true
  const showPageSelector = props.showPageSelector === undefined ? true : props.showPageSelector; // default true
  const pageSizeOptions = props.pageSizeOptions || [10, 20, 50, 100];
  const totalPageCount = Math.ceil(props.total / props.pageSize);
  const offset = Math.min(Math.max(0, props.page - 3), Math.max(0, totalPageCount - 5));

  return (
    <Flex
      justify="space-between"
      align="center"
      className={props.sticky ? styles.stickyPagination : ''}
    >
      <Flex align="center" gap={4}>
        {showPageSizeOptions && (
          <Select
            prefer="above"
            defaultOption={props.pageSize}
            options={pageSizeOptions}
            optionRender={(option) => {
              return <p>{option} results</p>;
            }}
            onChange={(v) => props.onChangePageSize(v)}
          />
        )}
        <span>{`${startIndex}-${endIndex} of ${props.total} results`}</span>
      </Flex>
      <Flex align="center" gap={4}>
        {totalPageCount > 1 && showPageSelector && (
          <Flex gap={2} align="center" style={{ maxWidth: "200px" }}>
            <label style={{ minWidth: "max-content" }} htmlFor="gotoPage">Go to page:</label>
            <InputNumber
              inputId="gotoPage"
              min={1}
              max={totalPageCount}
              value={props.page}
              onChange={e => {
                props.onChangePage(e)
              }}
              showControls={false}
            />
          </Flex>
        )}
        <Flex gap={0.8}>
          <Button
            type="text"
            disabled={props.page < 2}
            onClick={() => props.onChangePage(clamp(props.page - 1, 1, totalPageCount))}
          >
            <IconArrowLeft size={32} />
          </Button>

          {(totalPageCount > 5 && offset > 0) && (
            <>
              <Button
                type={props.page === 1 ? "primary" : "textButton"}
                onClick={() => props.onChangePage(1)}
              >
                <span className={styles.pageNumber}>1</span>
              </Button>
              {offset > 1 && <span>...</span>}
            </>
          )}

          {Array(Math.min(5, totalPageCount)).fill(1).map((_, i) => (
            <Button
              key={i}
              className={styles.pageButton}
              type={props.page === (i + 1 + offset) ? "primary" : "textButton"}
              onClick={() => props.onChangePage(i + 1 + offset)}
            >
              <span className={styles.pageNumber}>{i + 1 + offset}</span>
            </Button>
          ))}

          {totalPageCount > 5 && (
            <>
              {Math.abs(totalPageCount - offset) > 5 && (<span>...</span>)}
              {offset < (totalPageCount - 5) && (
                <Button
                  type={props.page === totalPageCount ? "primary" : "textButton"}
                  onClick={() => props.onChangePage(totalPageCount)}
                >
                  <span className={styles.pageNumber}>{totalPageCount}</span>
                </Button>
              )}
            </>
          )}

          <Button
            type="text"
            disabled={props.page >= totalPageCount && !props.allowEmptyNext}
            onClick={() => {
              if (props.allowEmptyNext) {
                props.onChangePage(props.page + 1);
              } else {
                props.onChangePage(clamp(props.page + 1, 1, totalPageCount));
              }
            }}
          >
            <IconArrow size={32} />
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Pagination;
